import { useMemo } from 'react'
import { User } from './AuthenticatedUser.context'
import { Permission } from './permissions.constants'
import { useUser } from './useUser'
import { getRequiredPermissionsForPage } from './pagePermissionUtils'

export interface AuthenticatedUserResult {
  user: User | null,
  hasPermission: (permission: Permission) => boolean,
  hasAnyPermission: (permissions: readonly Permission[]) => boolean,
  hasAllPermissions: (permissions: readonly Permission[]) => boolean,
  canAccessRoute: (route: string) => boolean,
}

export function useAuthenticatedUser(): AuthenticatedUserResult {
  const user = useUser()
  const userPermissions = useMemo(() => new Set(user?.permissions), [user])

  return {
    user,
    hasPermission: permission => userPermissions.has(permission),
    hasAnyPermission: permissions => permissions.some(permission => userPermissions.has(permission)),
    hasAllPermissions: permissions => permissions.every(permission => userPermissions.has(permission)),
    canAccessRoute: (route: string) => {
      const requiredPermissions = getRequiredPermissionsForPage(route)
      return requiredPermissions.length === 0 || requiredPermissions.some(permission => userPermissions.has(permission))
    },
  }
}
